import {AxiosError} from 'axios';
import {useState} from 'react';
import {MutationKey, useMutation, UseMutationOptions, UseMutationResult} from 'react-query';
import {GenericErrorResponse} from '../typings';
import {useApiErrorHandlers} from './useApiErrorHandlers';

export type UseCustomMutationReturn<TData, TError, TVariables, TContext> = UseMutationResult<TData,
    TError,
    TVariables,
    TContext> & {
  /**
   * Return customized error message
   */
  customErrorMsg: string;

  /**
   * Indicate the api is authorized
   */
  authorized: boolean;

  /**
   * Return extra mutation success count, currently library only provide failure count
   */
  successCount: number;

  /**
   * Wrapper reset function, to reset _successCount as well
   */
  reset: () => void;
};

export function useCustomMutation<TData = unknown,
    TVariables = void,
    TError = GenericErrorResponse,
    TContext = unknown>(
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: UseMutationOptions<TData, AxiosError<TError>, TVariables, TContext>,
    validateSession = true,
    toRedirect = true,
    key?: MutationKey
): UseCustomMutationReturn<TData, AxiosError<TError>, TVariables, TContext> {
  const [mutationSuccessCount, setMutationSuccessCount] = useState(0);
  const {sessionErrorHandler, apiErrorHandler} = useApiErrorHandlers();

  const mutationReturn = useMutation<TData, AxiosError<TError>, TVariables, TContext>({
    mutationKey: key,
    mutationFn,
    ...options,
    onSuccess: (...inputArgs) => {
      setMutationSuccessCount((prev) => prev + 1);
      return options?.onSuccess?.(...inputArgs);
    },
  });

  return {
    ...mutationReturn,
    authorized: validateSession ? sessionErrorHandler(mutationReturn.error, toRedirect) : true,
    customErrorMsg: apiErrorHandler(mutationReturn.error, toRedirect),
    successCount: mutationSuccessCount,
    reset: () => {
      setMutationSuccessCount(0);
      mutationReturn.reset();
    },
  };
}
